import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'
import Select from 'react-select'
import { productionAPI } from '../api/productionAPI'
import { brandAPI } from '../api/brandAPI'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { highlightText, formatDateTime } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import SearchInputWithClear from '../components/SearchInputWithClear'
import { usePermissions } from '../contexts/PermissionContext'
import CopyableText from '../components/CopyableText'
import StockItemInfo from '../components/StockItemInfo'
import '../styles/selectControl.css'

function ProductionListScreen() {
    const { hasPermission } = usePermissions()
    const [productionLots, setProductionLots] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [statusFilter, setStatusFilter] = useState('')
    const [selectedBrand, setSelectedBrand] = useState('')
    const [brandMap, setBrandMap] = useState({})
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = localStorage.getItem('productionColumnWidths');
        return savedWidths ? JSON.parse(savedWidths) : {
            lot_number: 'auto',
            image: 70,
            gtin: 'auto',
            recipe_name: 'auto',
            recipe_item_number: 'auto',
            brand: 'auto',
            status: 'auto',
            started_at: 'auto',
            target_quantity: 'auto',
            production_location_name: 'auto',
            actions: 'auto'
        };
    });

    useEffect(() => {
        localStorage.setItem('productionColumnWidths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    useEffect(() => {
        async function fetchBrands() {
            try {
                const response = await brandAPI.getAll(1, 1000, '', false)
                const brands = response.results || []
                const mapping = brands.reduce((acc, brand) => {
                    acc[brand._id] = brand.name
                    return acc
                }, {})
                setBrandMap(mapping)
            } catch (error) {
                console.error('Error fetching brands:', error)
            }
        }
        fetchBrands()
    }, [])

    useEffect(() => {
        async function fetchProductionLots() {
            try {
                setLoading(true)
                const response = await productionAPI.getAll(currentPage, pageSize, statusFilter, searchTerm, selectedBrand || undefined)
                setProductionLots(response.results || [])
                setTotalPages(response.count || 0)
            } finally {
                setLoading(false)
            }
        }
        fetchProductionLots()
    }, [currentPage, pageSize, searchTerm, statusFilter, selectedBrand])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const formatBrandOptions = () => {
        return Object.entries(brandMap).map(([id, name]) => ({
            value: id,
            label: name
        }));
    };

    const handleBrandFilterChange = (selectedOption) => {
        setSelectedBrand(selectedOption ? selectedOption.value : '');
        setCurrentPage(1);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'in_progress':
                return <Badge bg="primary">In Progress</Badge>
            case 'completed':
                return <Badge bg="success">Completed</Badge>
            case 'cancelled':
                return <Badge bg="secondary">Cancelled</Badge>
            default:
                return <Badge bg="light" text="dark">{status}</Badge>
        }
    }

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Production LOTs</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search by LOT number, recipe, item #..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col xs="auto">
                    <div className="d-flex gap-1">
                        <Button
                            variant={statusFilter === 'in_progress' ? 'primary' : 'outline-primary'}
                            onClick={() => {
                                setStatusFilter('in_progress');
                                setCurrentPage(1);
                            }}
                        >
                            In Progress
                        </Button>
                        <Button
                            variant={statusFilter === 'completed' ? 'success' : 'outline-success'}
                            onClick={() => {
                                setStatusFilter('completed');
                                setCurrentPage(1);
                            }}
                        >
                            Completed
                        </Button>
                        <Button
                            variant={statusFilter === 'cancelled' ? 'warning' : 'outline-warning'}
                            onClick={() => {
                                setStatusFilter('cancelled');
                                setCurrentPage(1);
                            }}
                        >
                            Cancelled
                        </Button>
                        <Button
                            variant={statusFilter === '' ? 'info' : 'outline-info'}
                            onClick={() => {
                                setStatusFilter('');
                                setCurrentPage(1);
                            }}
                        >
                            All
                        </Button>
                    </div>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        tableId="production_table"
                        columns={[
                            {
                                id: 'lot_number',
                                header: 'LOT Number',
                                cell: (row) => highlightText(row.lot_number, searchTerm)
                            },
                            {
                                id: 'image',
                                header: 'Image',
                                cell: (row) => row.output_stock_item_data?.image ? (
                                    <OverlayTrigger
                                        placement="right"
                                        overlay={
                                            <Tooltip className="stock-item-tooltip">
                                                <StockItemInfo stockItem={row.output_stock_item_data} />
                                            </Tooltip>
                                        }
                                    >
                                        <img
                                            src={row.output_stock_item_data.image}
                                            alt="Output Product"
                                            style={{
                                                width: '50px',
                                                height: '50px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </OverlayTrigger>
                                ) : null
                            },
                            {
                                id: 'gtin',
                                header: 'GTIN',
                                cell: (row) => <CopyableText value={row.output_stock_item_gtin} toastLabel="GTIN" searchTerm={searchTerm} />
                            },
                            {
                                id: 'recipe_name',
                                header: 'Recipe',
                                cell: (row) => highlightText(row.recipe_name || '-', searchTerm)
                            },
                            {
                                id: 'recipe_item_number',
                                header: 'Item #',
                                cell: (row) => row.recipe_item_number ? <CopyableText value={row.recipe_item_number} toastLabel="Item Number" searchTerm={searchTerm} /> : ''
                            },
                            {
                                id: 'brand',
                                header: () => (
                                    <div style={{ minWidth: '200px' }} className="compact-select">
                                        <Select
                                            value={selectedBrand ? { value: selectedBrand, label: brandMap[selectedBrand] } : null}
                                            onChange={handleBrandFilterChange}
                                            options={formatBrandOptions()}
                                            isSearchable
                                            isClearable
                                            placeholder="Brand"
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                        />
                                    </div>
                                ),
                                cell: (row) => row.output_stock_item_brand_name || ''
                            },
                            {
                                id: 'status',
                                header: 'Status',
                                cell: (row) => getStatusBadge(row.status)
                            },
                            {
                                id: 'started_at',
                                header: 'Started',
                                cell: (row) => formatDateTime(row.started_at)
                            },
                            {
                                id: 'target_quantity',
                                header: 'Target Qty',
                                cell: (row) => row.target_quantity
                            },
                            {
                                id: 'actual_quantity_produced',
                                header: 'Actual Qty',
                                cell: (row) => row.actual_quantity_produced != null ? row.actual_quantity_produced : ''
                            },
                            {
                                id: 'production_location_name',
                                header: 'Location',
                                cell: (row) => row.production_location_name || '-'
                            },
                            {
                                id: 'actions',
                                header: 'Actions',
                                cell: (row) => (
                                    <div style={{ display: 'flex', gap: '4px', whiteSpace: 'nowrap' }}>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                navigate(`/production/${row._id}`)
                                            }}
                                        >
                                            View
                                        </Button>
                                        {row.status === 'in_progress' && hasPermission('production.finalize') && (
                                            <Button
                                                variant="success"
                                                size="sm"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/production/${row._id}/finalize`)
                                                }}
                                            >
                                                Finalize
                                            </Button>
                                        )}
                                    </div>
                                )
                            }
                        ]}
                        data={productionLots}
                        initialColumnWidths={columnWidths}
                        onRowClick={(row) => navigate(`/production/${row._id}`)}
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default ProductionListScreen
